import { TableIcon } from "@heroicons/react/outline";
import { useId, useMemo } from "react";

import { SCORECARD_FIELD_LABEL } from "../../../constants";

import { Label } from "@/components/ui/label";
import { SearchableDropdown } from "@/components/ui/searchable-dropdown";

export interface ScorecardSourceOption {
  id: string;
  title: string;
}

interface ScorecardSourceSelectorProps {
  value: string;
  options: ScorecardSourceOption[];
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
}

interface ScorecardSourceItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * Column picker for scorecard evaluators, backed by the app-wide
 * `SearchableDropdown` combobox. Selection only happens on an explicit
 * item click or Enter — never as a side effect of blur/close.
 */
export const ScorecardSourceSelector = ({
  value,
  options,
  onValueChange,
  placeholder = "Select a source...",
  disabled = false,
  name,
}: ScorecardSourceSelectorProps) => {
  const triggerId = useId();

  const categories = useMemo(
    () => [
      {
        label: "Columns",
        items: options.map(option => ({
          id: option.id,
          label: option.title,
          icon: TableIcon,
        })),
      },
    ],
    [options],
  );

  const selectedItem = useMemo<ScorecardSourceItem | undefined>(() => {
    if (!value) {
      return undefined;
    }
    const selectedOption = options.find(option => option.id === value);
    if (selectedOption) {
      return { id: selectedOption.id, label: selectedOption.title, icon: TableIcon };
    }
    // The referenced column was deleted or is out of scope; surface a
    // clearable "unavailable" state instead of an empty trigger.
    return {
      id: `unavailable-${value}`,
      label: "Unavailable source",
      description: "The referenced column is no longer available",
      icon: TableIcon,
    };
  }, [options, value]);

  return (
    <div className="w-full space-y-2">
      {name ? (
        <Label htmlFor={triggerId} className={SCORECARD_FIELD_LABEL}>
          {name}
        </Label>
      ) : null}
      <SearchableDropdown
        categories={categories}
        selectedItem={selectedItem}
        placeholder={placeholder}
        emptyMessage="No sources found."
        removeable
        readonly={disabled}
        onSelect={(item: ScorecardSourceItem | undefined) => {
          // Items come from `categories` (real column ids) or `undefined`
          // when the user clears via the trigger's remove button.
          onValueChange(item?.id ?? "");
        }}
        triggerClassName="bg-white"
      />
    </div>
  );
};
